// pages/TicketManagement/components/TicketViewModal.jsx
import React, { useState, useEffect } from "react";
import { useSession } from "hooks/useSession";
import { useRolePermissions } from "./useRolePermissions";
import { TicketManagement } from "./ticketManagement";
import { fetchWrapper } from "utils/apiFetch";

const S = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 1060,
    background: "rgba(0,0,0,0.78)", backdropFilter: "blur(5px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "16px", animation: "fadeIn .18s ease",
  },
  modal: {
    background: "#111318", border: "1px solid #262c3a", borderRadius: "16px",
    width: "100%", maxWidth: "820px", maxHeight: "90vh",
    display: "flex", flexDirection: "column",
    boxShadow: "0 24px 80px rgba(0,0,0,.65)",
    animation: "slideUp .22s cubic-bezier(.34,1.56,.64,1)",
  },
  header: {
    padding: "20px 28px 16px", borderBottom: "1px solid #262c3a",
    display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0,
  },
  body: { display: "grid", gridTemplateColumns: "1fr 280px", flex: 1, overflow: "hidden" },
  left: { overflowY: "auto", padding: "22px 24px", borderRight: "1px solid #262c3a" },
  right: { overflowY: "auto", padding: "20px 20px", background: "#0e1016" },
  badge: (color, bg) => ({
    display: "inline-flex", alignItems: "center", gap: "5px",
    background: bg, color, fontSize: "11px", fontWeight: 700,
    padding: "3px 10px", borderRadius: "20px", whiteSpace: "nowrap",
  }),
  metaCard: {
    background: "#181c24", border: "1px solid #262c3a",
    borderRadius: "10px", padding: "14px 16px", marginBottom: "12px",
  },
  metaLabel: {
    fontSize: "10px", fontWeight: 700, letterSpacing: "1px",
    textTransform: "uppercase", color: "#525d72", marginBottom: "5px",
  },
  metaValue: { fontSize: "13.5px", fontWeight: 500, color: "#e8eaf0" },
  mono: { fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: "#00d4aa" },
  sectionHead: {
    fontSize: "11px", fontWeight: 700, letterSpacing: "1px",
    textTransform: "uppercase", color: "#8892a4",
    marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px",
  },
  commentItem: (internal) => ({
    background: internal ? "rgba(245,158,11,.06)" : "#181c24",
    border: `1px solid ${internal ? "rgba(245,158,11,.25)" : "#262c3a"}`,
    borderLeft: internal ? "3px solid #f59e0b" : "1px solid #262c3a",
    borderRadius: "8px", padding: "12px 14px", marginBottom: "10px",
  }),
  commentInput: {
    width: "100%", background: "#0a0c10", border: "1px solid #262c3a",
    borderRadius: "7px", color: "#e8eaf0", fontSize: "13px",
    padding: "9px 12px", resize: "vertical", minHeight: "70px",
    outline: "none", fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box",
  },
  statusBtn: (active) => ({
    width: "100%", padding: "8px 12px", borderRadius: "7px",
    border: active ? "1px solid #00d4aa" : "1px solid #262c3a",
    background: active ? "rgba(0,212,170,.1)" : "transparent",
    color: active ? "#00d4aa" : "#8892a4",
    fontSize: "12.5px", fontWeight: 600, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: "6px", transition: "all .15s",
    fontFamily: "'DM Sans', sans-serif",
  }),
  closeBtn: {
    background: "none", border: "none", cursor: "pointer",
    color: "#525d72", fontSize: "20px", lineHeight: 1,
    padding: "4px 8px", borderRadius: "6px",
  },
  submitCommentBtn: {
    background: "#00d4aa", border: "none", borderRadius: "7px",
    color: "#000", padding: "8px 18px", fontSize: "13px", fontWeight: 700,
    cursor: "pointer", marginTop: "10px", display: "flex", alignItems: "center", gap: "6px",
    fontFamily: "'DM Sans', sans-serif",
  },
};

const BadgePriority = ({ val }) => {
  const m = TicketManagement.badgeMap.priority[val] || { color: "#8892a4", bg: "#1e2330", label: val };
  return <span style={S.badge(m.color, m.bg)}>{m.label}</span>;
};
const BadgeStatus = ({ val }) => {
  const m = TicketManagement.badgeMap.status[val] || { color: "#8892a4", bg: "#1e2330", label: val };
  return <span style={S.badge(m.color, m.bg)}>{m.label}</span>;
};

/* ═══════════════════════════════════════════
   MAIN
═══════════════════════════════════════════ */
const TicketViewModal = ({ show, onClose, ticket, onStatusChange, onEditOpen }) => {
  const { userid } = useSession();
  const { can, roleName, isAdmin } = useRolePermissions(TicketManagement);
  const [comments, setComments]   = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [posting, setPosting]     = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    if (!show || !ticket?.id) return;
    setComments([]);
    setNewComment("");
    const fd = new FormData();
    fd.append("action", "GET_COMMENTS");
    fd.append("ticket_id", ticket.id);
    fd.append("userid", userid);
    fetchWrapper(TicketManagement.apiUrl, { method: "POST", body: fd })
      .then(r => setComments(r?.data || []))
     
      .catch(() => {});
  }, [show, ticket?.id, userid]);

  const handlePostComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    const fd = new FormData();
    fd.append("action", "ADD_COMMENT");
    fd.append("ticket_id", ticket.id);
    fd.append("userid", userid);
    fd.append("content", newComment);
    fd.append("is_internal", isInternal ? "1" : "0");
    try {
      const d = await fetchWrapper(TicketManagement.apiUrl, { method: "POST", body: fd });
     
      if (d?.status === "success" || d?.success) {
        setComments(prev => [...prev, d.comment || {
          id: Date.now(), content: newComment, is_internal: isInternal,
          author_name: "You", created_at: new Date().toISOString(),
        }]);
        setNewComment("");
      }
    } catch {}
    setPosting(false);
  };

  const handleQuickStatus = async (newStatus) => {
    if (newStatus === ticket?.status || statusLoading) return;
    setStatusLoading(true);
    const fd = new FormData();
    fd.append("action", "UPDATE_STATUS");
    fd.append("ticket_id", ticket.id);
    fd.append("status", newStatus);
    fd.append("userid", userid);
    try {
      const d = await fetchWrapper(TicketManagement.apiUrl, { method: "POST", body: fd });
    //   const d = await r.json();
      if (d?.status === "success" || d?.success) onStatusChange?.(newStatus);
    } catch {}
    setStatusLoading(false);
  };

  if (!show || !ticket) return null;

  const tagList = (ticket.tags || "").split(",").map(t => t.trim()).filter(Boolean);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes slideUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .tv-status-btn:hover { border-color:#00d4aa!important; color:#e8eaf0!important; background:#181c24!important; }
        .tv-close:hover { color:#e8eaf0!important; background:#262c3a!important; }
      `}</style>

      <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={S.modal}>

          {/* ── Header ── */}
          <div style={S.header}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "8px" }}>
                <span style={{ ...S.mono, fontSize: "14px" }}>{ticket.ticket_id || `#${ticket.id}`}</span>
                <BadgePriority val={ticket.priority} />
                <BadgeStatus val={ticket.status} />
                <span style={{ ...S.badge("#a78bfa", "rgba(167,139,250,.12)") }}>
                  {TicketManagement.ticketTypeOptions.find(o => o.value === ticket.ticket_type)?.label || ticket.ticket_type}
                </span>
              </div>
              <h2 style={{ fontSize: "16px", fontWeight: 700, color: "#e8eaf0", margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
                {ticket.title}
              </h2>
            </div>
            <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
              {can.edit && (
                <button onClick={onEditOpen} style={{
                  background: "transparent", border: "1px solid #262c3a",
                  borderRadius: "7px", color: "#8892a4", padding: "7px 14px",
                  fontSize: "12.5px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                }}>
                  ✏️ Edit
                </button>
              )}
              <button style={S.closeBtn} className="tv-close" onClick={onClose}>✕</button>
            </div>
          </div>

          {/* ── Body ── */}
          <div style={S.body}>

            {/* ── LEFT: Description + Comments ── */}
            <div style={S.left}>

              {/* Description */}
              <div style={{ marginBottom: "20px" }}>
                <div style={S.sectionHead}>📄 Description</div>
                <div style={{
                  fontSize: "14px", color: "#8892a4", lineHeight: "1.75",
                  background: "#181c24", border: "1px solid #262c3a",
                  borderRadius: "8px", padding: "14px 16px",
                }}>
                  {ticket.description || <em style={{ color: "#525d72" }}>No description provided.</em>}
                </div>
              </div>

              {/* Financial Info */}
              {(ticket.amount_involved || ticket.reference_number) && (
                <div style={{ marginBottom: "20px" }}>
                  <div style={S.sectionHead}>💰 Financial Details</div>
                  <div style={{
                    background: "rgba(0,212,170,.05)", border: "1px solid rgba(0,212,170,.15)",
                    borderRadius: "8px", padding: "14px 16px",
                    display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px",
                  }}>
                    {ticket.amount_involved && (
                      <div>
                        <div style={S.metaLabel}>Amount</div>
                        <div style={{ fontSize: "20px", fontWeight: 700, color: "#00d4aa", fontFamily: "'IBM Plex Mono', monospace" }}>
                          {ticket.currency || "INR"} {Number(ticket.amount_involved).toLocaleString("en-IN")}
                        </div>
                      </div>
                    )}
                    {ticket.reference_number && (
                      <div>
                        <div style={S.metaLabel}>Reference / TXN</div>
                        <div style={{ ...S.mono, fontSize: "13px", color: "#e8eaf0" }}>{ticket.reference_number}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              {tagList.length > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <div style={S.sectionHead}>🏷 Tags</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {tagList.map((tag, i) => (
                      <span key={i} style={{
                        background: "#1e2330", border: "1px solid #262c3a",
                        color: "#8892a4", fontSize: "11px", padding: "3px 10px", borderRadius: "20px",
                      }}>
                        # {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments Thread */}
              <div>
                <div style={S.sectionHead}>
                  💬 Comments
                  <span style={{ background: "#1e2330", color: "#8892a4", fontSize: "10px", padding: "2px 7px", borderRadius: "10px" }}>
                    {comments.length}
                  </span>
                </div>

                {comments.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "24px", color: "#525d72", fontSize: "13px" }}>
                    <div style={{ fontSize: "28px", marginBottom: "8px", opacity: .3 }}>💬</div>
                    No comments yet.
                  </div>
                ) : (
                  comments.map((c, i) => (
                    <div key={c.id || i} style={S.commentItem(c.is_internal)}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "7px" }}>
                        <div style={{
                          width: "22px", height: "22px", borderRadius: "50%",
                          background: "rgba(0,212,170,.15)", color: "#00d4aa",
                          fontSize: "10px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {(c.author_name || "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: "13px", fontWeight: 600, color: "#e8eaf0" }}>{c.author_name}</span>
                        <span style={{ fontSize: "11px", color: "#525d72", fontFamily: "'IBM Plex Mono', monospace" }}>
                          {c.created_at ? new Date(c.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}
                        </span>
                        {c.is_internal && (
                          <span style={{ fontSize: "10px", fontWeight: 700, color: "#f59e0b", background: "rgba(245,158,11,.12)", padding: "1px 6px", borderRadius: "4px" }}>
                            INTERNAL
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "13.5px", color: "#8892a4", lineHeight: "1.6" }}>
                        {c.content}
                      </div>
                    </div>
                  ))
                )}

                {/* Add Comment */}
                <div style={{ marginTop: "16px", paddingTop: "14px", borderTop: "1px solid #262c3a" }}>
                  <textarea
                    value={newComment} onChange={e => setNewComment(e.target.value)}
                    placeholder="Add a comment or update..."
                    style={S.commentInput} className="tm-input"
                  />
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "7px", cursor: "pointer", fontSize: "12.5px", color: "#8892a4" }}>
                      <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)}
                        style={{ accentColor: "#f59e0b" }} />
                      Internal note (not visible to customer)
                    </label>
                    <button style={S.submitCommentBtn} onClick={handlePostComment} disabled={posting || !newComment.trim()}>
                      {posting ? "Posting..." : "➤ Post"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── RIGHT: Meta Panel ── */}
            <div style={S.right}>

              {/* Meta */}
              <div style={S.sectionHead}>📋 Details</div>

              {[
                { label: "Ticket ID",   value: ticket.ticket_id || `#${ticket.id}`, mono: true },
                { label: "Created By",  value: ticket.created_by_name || "—" },
                { label: "Assigned To", value: ticket.assigned_to_name || "Unassigned" },
                { label: "Category",    value: ticket.category || "—" },
                { label: "Created",     value: ticket.created_at ? new Date(ticket.created_at).toLocaleDateString("en-IN") : "—" },
                { label: "Updated",     value: ticket.updated_at ? new Date(ticket.updated_at).toLocaleDateString("en-IN") : "—" },
                ...(ticket.due_date ? [{ label: "Due Date", value: new Date(ticket.due_date).toLocaleString("en-IN"), warn: true }] : []),
                ...(ticket.resolved_at ? [{ label: "Resolved At", value: new Date(ticket.resolved_at).toLocaleString("en-IN"), success: true }] : []),
              ].map(({ label, value, mono, warn, success }) => (
                <div key={label} style={S.metaCard}>
                  <div style={S.metaLabel}>{label}</div>
                  <div style={{
                    ...S.metaValue,
                    ...(mono ? S.mono : {}),
                    ...(warn ? { color: "#f59e0b" } : {}),
                    ...(success ? { color: "#10b981" } : {}),
                  }}>{value}</div>
                </div>
              ))}

              {/* Quick Status — admins only */}
              {(isAdmin || can.edit) && (
                <>
                  <div style={{ ...S.sectionHead, marginTop: "16px" }}>⚡ Quick Status</div>
                  {TicketManagement.statusOptions.map(opt => {
                    const isActive = ticket.status === opt.value;
                    const m = TicketManagement.badgeMap.status[opt.value];
                    return (
                      <button
                        key={opt.value}
                        style={S.statusBtn(isActive)}
                        className="tv-status-btn"
                        onClick={() => handleQuickStatus(opt.value)}
                        disabled={statusLoading}
                      >
                        <span style={isActive ? S.badge(m?.color, m?.bg) : { color: "#8892a4" }}>
                          {opt.label}
                        </span>
                        {isActive && <span style={{ color: "#00d4aa", fontSize: "13px" }}>✓</span>}
                      </button>
                    );
                  })}
                </>
              )}

              {/* Role indicator */}
              <div style={{
                marginTop: "16px", padding: "10px 12px",
                background: "rgba(0,212,170,.05)", border: "1px solid rgba(0,212,170,.1)",
                borderRadius: "8px", fontSize: "11px", color: "#525d72",
              }}>
                Viewing as <strong style={{ color: "#00d4aa" }}>{roleName.replace(/_/g, " ").toUpperCase()}</strong>
                {!can.edit && <div style={{ marginTop: "4px", color: "#f59e0b" }}>⚠ View-only access</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketViewModal;